// Kalkulator podatków — migracja z FormAssembly do React
// 2026 update: nowezdrowotneryczalt uses PW2026 + MW2026 minimum

export const CALC_2026 = {
  MW2026: 4806,
  // GUS (22 stycznia 2026): przeciętne miesięczne wynagrodzenie w sektorze przedsiębiorstw w IV kw. 2025
  PW2026: 9228.64,
  HEALTH_MIN_MONTHLY: 432.54, // MW2026 * 0.09 (min. dla zasad ogólnych / liniowego)
  LINIOWY_HEALTH_MIN_MONTHLY: 432.54,
  OGOLNE_HEALTH_MIN_MONTHLY: 432.54,
  RYCZALT_THRESHOLDS_ANNUAL: {
    t1: 60000,
    t2: 300000,
  },
};

export const RYCZALT_OPTIONS = [
  { label: "17%", value: 0.17 },
  { label: "15%", value: 0.15 },
  { label: "14%", value: 0.14 },
  { label: "12,5%", value: 0.125 },
  { label: "12%", value: 0.12 },
  { label: "10%", value: 0.1 },
  { label: "8,5%", value: 0.085 },
  { label: "5,5%", value: 0.055 },
  { label: "3%", value: 0.03 },
];

export function parsePLNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value)
    .trim()
    .replace(/\s+/g, "")
    .replace(/,/g, ".");
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function clampToNumber(n, fallback = 0) {
  return Number.isFinite(n) ? n : fallback;
}

export function formatPLN(n, { maximumFractionDigits = 2 } = {}) {
  const num = clampToNumber(n, 0);
  return new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(num);
}

export function formatPercent(n) {
  const num = clampToNumber(n, 0);
  return `${new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num)}%`;
}

export function findRyczaltOptionByPercent(percent) {
  const target = clampToNumber(percent, 0) / 100;
  const eps = 0.00001;
  const exact = RYCZALT_OPTIONS.find((o) => Math.abs(o.value - target) < eps);
  if (exact) return exact.value;

  // fallback: najbliższa opcja
  let best = RYCZALT_OPTIONS[0]?.value ?? 0;
  let bestDiff = Infinity;
  for (const opt of RYCZALT_OPTIONS) {
    const d = Math.abs(opt.value - target);
    if (d < bestDiff) {
      bestDiff = d;
      best = opt.value;
    }
  }
  return best;
}

// Odtworzenie logiki FormAssembly (pola readonly + formuły)
export function computeCalculator({
  ryczaltRate, // np. 0.085
  przychodMonthly,
  kosztyMonthly,
  rocznyPrzychod,
  zusmalyMonthly,
  zusduzyMonthly,
}) {
  const przychod = clampToNumber(przychodMonthly, 0);
  const koszty = clampToNumber(kosztyMonthly, 0);
  const roczny = clampToNumber(rocznyPrzychod, 0);
  const ryczalt = clampToNumber(ryczaltRate, 0);
  const zusmaly = clampToNumber(zusmalyMonthly, 0);
  const zusduzy = clampToNumber(zusduzyMonthly, 0);

  // 2026 (GUS): zdrowotne ryczałt liczone od PW2026 i progów rocznych
  // wg publikacji: 60% / 100% / 180% PW2026 * 9%
  let base;
  if (roczny <= CALC_2026.RYCZALT_THRESHOLDS_ANNUAL.t1) {
    base = CALC_2026.PW2026 * 0.6;
  } else if (roczny <= CALC_2026.RYCZALT_THRESHOLDS_ANNUAL.t2) {
    base = CALC_2026.PW2026 * 1.0;
  } else {
    base = CALC_2026.PW2026 * 1.8;
  }
  const nowezdrowotneryczalt = base * 0.09;

  // 2026: minimum 432.54 dla ogólnych i liniowego
  const nowezdrowotneogolne = Math.max(
    (przychod - koszty) * 0.09,
    CALC_2026.OGOLNE_HEALTH_MIN_MONTHLY,
  );

  const nowezdrowotneliniowy = Math.max(
    (przychod - koszty) * 0.049,
    CALC_2026.LINIOWY_HEALTH_MIN_MONTHLY,
  );

  // Formuły 1:1 z HTML (miesięczne)
  const nowepodatek = ryczalt * (przychod - nowezdrowotneryczalt / 2 - zusmaly - zusduzy);

  const nowyladzo =
    (przychod - koszty - zusmaly - zusduzy) * 12 <= 120000
      ? (przychod - koszty - zusmaly - zusduzy) * 0.12 - 300
      : (przychod - koszty - zusmaly - zusduzy) * 0.32 - 2000 - 300;

  const nowelintax = (przychod - koszty - nowezdrowotneliniowy - zusmaly - zusduzy) * 0.19;

  const limit10200 =
    nowezdrowotneliniowy * 12 >= 10200
      ? (nowezdrowotneliniowy * 12 - 10200) * 0.19
      : 0;

  const zostajeRyczalt = przychod - koszty - nowepodatek - zusmaly - zusduzy - nowezdrowotneryczalt;
  const zostajeOgolne = przychod - koszty - nowyladzo - zusmaly - zusduzy - nowezdrowotneogolne;
  const zostajeLiniowy = przychod - koszty - nowelintax - zusmaly - zusduzy - nowezdrowotneliniowy;

  return {
    nowezdrowotneryczalt,
    nowezdrowotneogolne,
    nowezdrowotneliniowy,
    nowepodatek,
    nowyladzo,
    nowelintax,
    limit10200,
    zostajeRyczalt,
    zostajeOgolne,
    zostajeLiniowy,
  };
}
